import React, { useState } from 'react'

export const ResizeHandle = ({ position, onMouseDown, onTouchStart }) => {
  const [isFocused, setIsFocused] = useState(false)
  const [cursorY, setCursorY] = useState(null)
  const [showTooltip, setShowTooltip] = useState(false)
  const [isHovered, setIsHovered] = useState(false)
  const tooltipTimerRef = React.useRef(null)
  const mouseDownPosition = React.useRef(null)

  const handleMouseMove = e => {
    setCursorY(e.clientY)
  }

  const handleMouseEnter = e => {
    setIsHovered(true)
    setCursorY(e.clientY)

    // Show tooltip after 500ms delay
    tooltipTimerRef.current = setTimeout(() => {
      setShowTooltip(true)
    }, 500)
  }

  const handleMouseLeave = () => {
    setIsHovered(false)
    setCursorY(null)
    setShowTooltip(false)

    // Clear the timer if user leaves before tooltip appears
    if (tooltipTimerRef.current) {
      clearTimeout(tooltipTimerRef.current)
      tooltipTimerRef.current = null
    }
  }

  const handleMouseDown = e => {
    // Store the mouse position when mouse down occurs
    mouseDownPosition.current = { x: e.clientX, y: e.clientY }

    // Hide tooltip immediately when mouse button is pressed
    setShowTooltip(false)
    if (tooltipTimerRef.current) {
      clearTimeout(tooltipTimerRef.current)
      tooltipTimerRef.current = null
    }

    // Call parent's onMouseDown
    if (onMouseDown) {
      onMouseDown(e)
    }
  }

  const handleClick = e => {
    // Check if this was a click (no drag movement)
    if (mouseDownPosition.current) {
      const deltaX = Math.abs(e.clientX - mouseDownPosition.current.x)
      const deltaY = Math.abs(e.clientY - mouseDownPosition.current.y)

      // If mouse moved less than 5px in either direction, treat it as a click
      if (deltaX < 5 && deltaY < 5) {
        window.dispatchEvent(
          new CustomEvent('close-left-drawer', {
            detail: { restoreFocus: false },
          })
        )
      }
    }

    mouseDownPosition.current = null
  }

  // Cleanup timer on unmount
  React.useEffect(() => {
    return () => {
      if (tooltipTimerRef.current) {
        clearTimeout(tooltipTimerRef.current)
      }
    }
  }, [])

  return (
    <React.Fragment>
      <style>{`
        .left-resize-handle .visual-bar {
          position: relative;
          width: 2px;
          height: 100%;
          background-color: #004876;
          opacity: 0;
          pointer-events: none;
          transition: opacity 0.2s ease;
        }

        .left-resize-handle:hover .visual-bar,
        .left-resize-handle:focus .visual-bar {
          opacity: 1;
        }

        body.resizing-left .left-resize-handle .visual-bar {
          opacity: 1 !important;
          transition: none !important;
        }
      `}</style>

      <div
        className="left-resize-handle"
        style={{ left: `${position}px` }}
        onMouseDown={handleMouseDown}
        onClick={handleClick}
        onTouchStart={onTouchStart}
        onMouseEnter={handleMouseEnter}
        onMouseMove={handleMouseMove}
        onMouseLeave={handleMouseLeave}
        onFocus={() => setIsFocused(true)}
        onBlur={() => setIsFocused(false)}
        role="separator"
        aria-orientation="vertical"
        aria-label="Drag to resize table of contents panel, or click to close"
        tabIndex={0}
        onKeyDown={e => {
          if (e.key === 'ArrowLeft' || e.key === 'ArrowRight') {
            e.preventDefault()
            const leftDrawer = document.getElementById('left-drawer')
            if (!leftDrawer) return
            const currentWidth = leftDrawer.offsetWidth
            const delta = e.key === 'ArrowRight' ? 10 : -10
            const newWidth = currentWidth + delta
            window.dispatchEvent(
              new CustomEvent('keyboard-resize', {
                detail: { newWidth, panel: 'left' },
              })
            )
          } else if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault()
            window.dispatchEvent(
              new CustomEvent('close-left-drawer', {
                detail: { restoreFocus: false },
              })
            )
          }
        }}
      >
        {/* Expanded hit area */}
        <div
          style={{
            position: 'absolute',
            top: 0,
            left: '-8px',
            right: '-8px',
            bottom: 0,
            cursor: 'col-resize',
          }}
        />

        {/* Visual bar - now controlled by CSS */}
        <div className="visual-bar" />

        {/* Tooltip that follows cursor */}
        {isHovered && showTooltip && cursorY && (
          <div
            style={{
              position: 'fixed',
              left: `${position + 20}px`,
              top: `${cursorY - 50}px`,
              transform: 'translateY(-50%)',
              backgroundColor: '#1e293b',
              color: '#fff',
              padding: '4px 8px',
              borderRadius: '4px',
              fontSize: '12px',
              fontWeight: 400,
              pointerEvents: 'none',
              boxShadow:
                '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
              zIndex: 1000,
              display: 'flex',
              flexDirection: 'column',
              gap: '2px',
            }}
          >
            <span style={{ whiteSpace: 'nowrap' }}>Drag to resize</span>
            <span style={{ whiteSpace: 'nowrap' }}>Click to close</span>
          </div>
        )}

        {isFocused && !isHovered && (
          <span
            style={{
              position: 'absolute',
              left: '12px',
              top: '50%',
              transform: 'translateY(-50%)',
              backgroundColor: '#1e293b',
              color: '#fff',
              padding: '4px 8px',
              borderRadius: '4px',
              fontSize: '12px',
              fontWeight: 400,
              whiteSpace: 'nowrap',
              pointerEvents: 'none',
              boxShadow:
                '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
              zIndex: 1000,
            }}
          >
            ← → to resize • Enter to close
          </span>
        )}
      </div>
    </React.Fragment>
  )
}
